/**
 * Chutt... V2 - ContentGenerator
 * Content generation form + result display.
 * Allows users to generate AI-powered brand content
 * for various platforms and content types.
 *
 * Props:
 *   - brandName: string
 *   - profileType: string
 *   - onGenerate: function({ topic, contentType, platform, brandName, profileType })
 *   - loading: boolean
 *   - generatedContent: string | null
 */
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  Skeleton,
  CircularProgress,
  Divider,
  Chip,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PaletteIcon from '@mui/icons-material/Palette';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import BrushIcon from '@mui/icons-material/Brush';
import { colors } from '../../styles/colors';

const CONTENT_TYPES = [
  { value: 'post_linkedin', label: 'Post LinkedIn' },
  { value: 'tweet', label: 'Tweet' },
  { value: 'communique', label: 'Communique de presse' },
  { value: 'article_blog', label: 'Article de blog' },
  { value: 'reponse_commentaire', label: 'Reponse commentaire' },
];

const PLATFORMS = [
  { value: 'linkedin', label: 'LinkedIn' },
  { value: 'twitter', label: 'Twitter/X' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'blog', label: 'Blog' },
];

const CONTENT_PLATFORM_MAP = {
  post_linkedin: 'linkedin',
  tweet: 'twitter',
  communique: null,
  article_blog: 'blog',
  reponse_commentaire: null,
};

const VISUAL_TYPE_LABELS = {
  photo_corporate: 'Photo corporate',
  infographie: 'Infographie',
  illustration: 'Illustration',
  portrait: 'Portrait',
  paysage: 'Photo paysage',
  graphique: 'Graphique',
  video_thumbnail: 'Miniature video',
};

const SOURCE_METHOD_LABELS = {
  shooting: 'Shooting photo',
  banque_images: 'Banque d\'images',
  creation_graphique: 'Creation graphique',
  ia_generative: 'IA generative',
};

function ContentGenerator({ brandName, profileType, onGenerate, loading, generatedContent, generatedVisual, initialTopic = '', initialPlatform = '', initialContentType = '', generatingStep = '' }) {
  const [topic, setTopic] = useState(initialTopic);
  const [contentType, setContentType] = useState(initialContentType || 'post_linkedin');
  const [platform, setPlatform] = useState(initialPlatform || 'linkedin');
  const [copied, setCopied] = useState(false);
  const [showSelectors, setShowSelectors] = useState(!initialContentType);
  const userChangedPlatform = useRef(false);

  useEffect(() => {
    if (initialTopic) setTopic(initialTopic);
  }, [initialTopic]);

  useEffect(() => {
    if (initialPlatform) {
      setPlatform(initialPlatform);
      userChangedPlatform.current = false;
    }
  }, [initialPlatform]);

  // Auto-set contentType from initialContentType prop + hide selectors
  useEffect(() => {
    if (initialContentType) {
      setContentType(initialContentType);
      userChangedPlatform.current = false;
      setShowSelectors(false);
    }
  }, [initialContentType]);

  // Bidirectional: when contentType changes, auto-set platform if user hasn't manually changed it
  useEffect(() => {
    if (userChangedPlatform.current) return;
    const mapped = CONTENT_PLATFORM_MAP[contentType];
    if (mapped && mapped !== platform) {
      setPlatform(mapped);
    }
  }, [contentType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = () => {
    if (onGenerate && topic.trim()) {
      onGenerate({
        topic: topic.trim(),
        contentType,
        platform,
        brandName,
        profileType,
      });
    }
  };

  const handleCopy = async () => {
    if (generatedContent) {
      try {
        await navigator.clipboard.writeText(generatedContent);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        // Fallback for browsers that don't support clipboard API
        const textarea = document.createElement('textarea');
        textarea.value = generatedContent;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (fallbackErr) {
          console.warn('Impossible de copier le contenu', fallbackErr);
        }
        document.body.removeChild(textarea);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && topic.trim() && !loading) {
      e.preventDefault();
      handleGenerate();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        bgcolor: colors.white,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 1.5,
            bgcolor: colors.accentBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: colors.accent }} />
        </Box>
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, color: colors.charcoal, fontSize: '1.1rem' }}
        >
          Generateur de contenu
        </Typography>
      </Box>

      {/* Form */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {/* Topic */}
        <TextField
          label="Sujet"
          placeholder="Ex: Lancement du nouveau produit, reponse a une critique..."
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          onKeyDown={handleKeyDown}
          fullWidth
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&.Mui-focused fieldset': { borderColor: colors.accent },
            },
            '& .MuiInputLabel-root.Mui-focused': { color: colors.accent },
          }}
        />

        {/* Content type + Platform: compact chips when auto-filled, dropdowns when manual */}
        {!showSelectors ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              label={CONTENT_TYPES.find(ct => ct.value === contentType)?.label || contentType}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: colors.accentBg,
                color: colors.accent,
              }}
            />
            <Chip
              label={PLATFORMS.find(p => p.value === platform)?.label || platform}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                bgcolor: colors.accentBg,
                color: colors.accent,
              }}
            />
            <IconButton
              size="small"
              aria-label="Modifier le type et la plateforme"
              onClick={() => setShowSelectors(true)}
              sx={{ color: colors.gray400, p: 0.3 }}
            >
              <Tooltip title="Modifier le type / plateforme">
                <EditIcon sx={{ fontSize: 14 }} />
              </Tooltip>
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: colors.accent } }}>
                Type de contenu
              </InputLabel>
              <Select
                value={contentType}
                label="Type de contenu"
                onChange={(e) => setContentType(e.target.value)}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                }}
              >
                {CONTENT_TYPES.map((ct) => (
                  <MenuItem key={ct.value} value={ct.value}>
                    {ct.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: colors.accent } }}>
                Plateforme
              </InputLabel>
              <Select
                value={platform}
                label="Plateforme"
                onChange={(e) => { userChangedPlatform.current = true; setPlatform(e.target.value); }}
                sx={{
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colors.accent,
                  },
                }}
              >
                {PLATFORMS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Generate button */}
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={!topic.trim() || loading}
          startIcon={
            loading
              ? <CircularProgress size={16} color="inherit" />
              : <SendIcon />
          }
          sx={{
            bgcolor: colors.accent,
            color: colors.white,
            fontWeight: 600,
            textTransform: 'none',
            borderRadius: 1.5,
            py: 1,
            '&:hover': { bgcolor: colors.accentDark },
            '&.Mui-disabled': { bgcolor: colors.gray300 },
          }}
        >
          {loading ? 'Generation en cours...' : 'Generer'}
        </Button>
        {loading && generatingStep && (
          <Typography variant="body2" sx={{ color: colors.accent, fontSize: '0.78rem', mt: 1, fontStyle: 'italic' }}>
            {generatingStep}
          </Typography>
        )}
      </Box>

      {/* Loading skeleton for result */}
      {loading && !generatedContent && (
        <Box sx={{ mt: 2 }}>
          <Skeleton variant="rounded" width="100%" height={140} />
        </Box>
      )}

      {/* Generated content result */}
      {generatedContent !== null && generatedContent !== undefined && !loading && (
        <Box
          sx={{
            animation: 'fadeInUp 0.4s ease-out',
            '@keyframes fadeInUp': {
              '0%': { opacity: 0, transform: 'translateY(8px)' },
              '100%': { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <Divider sx={{ mb: 2.5, borderColor: colors.gray200 }} />

          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
              color: colors.gray500,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mb: 1,
            }}
          >
            Contenu genere
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 1.5,
              bgcolor: colors.gray50,
              border: `1px solid ${colors.gray200}`,
              position: 'relative',
            }}
          >
            <Typography
              sx={{
                color: colors.charcoal,
                fontSize: '0.88rem',
                lineHeight: 1.7,
                whiteSpace: 'pre-wrap',
                fontFamily: "'Roboto Mono', 'Courier New', monospace",
                pr: 4,
              }}
            >
              {generatedContent}
            </Typography>

            {/* Copy button */}
            <Tooltip title={copied ? 'Copie !' : 'Copier'}>
              <IconButton
                onClick={handleCopy}
                size="small"
                aria-label={copied ? 'Copie' : 'Copier le contenu'}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  color: copied ? colors.success : colors.gray400,
                  bgcolor: colors.white,
                  border: `1px solid ${colors.gray200}`,
                  '&:hover': { bgcolor: colors.gray100 },
                }}
              >
                {copied ? (
                  <CheckIcon sx={{ fontSize: 16 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </Tooltip>
          </Paper>

          {/* Visual Recommendation */}
          {generatedVisual && (
            <Box sx={{ mt: 2.5 }}>
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: colors.gray500,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mb: 1,
                }}
              >
                Recommandation visuelle
              </Typography>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 1.5,
                  bgcolor: colors.ivory || '#FAF7F2',
                  border: `1px solid ${colors.gray200}`,
                  borderLeft: `3px solid ${colors.primary}`,
                }}
              >
                {/* Type + Format + Source badges */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  {generatedVisual.type && (
                    <Chip
                      icon={<CameraAltIcon sx={{ fontSize: 14 }} />}
                      label={VISUAL_TYPE_LABELS[generatedVisual.type] || generatedVisual.type}
                      size="small"
                      sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', fontWeight: 600, bgcolor: colors.primaryBg, color: colors.primary }}
                    />
                  )}
                  {generatedVisual.format && (
                    <Chip
                      icon={<AspectRatioIcon sx={{ fontSize: 14 }} />}
                      label={`Format ${generatedVisual.format}`}
                      size="small"
                      sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', fontWeight: 600, bgcolor: colors.gray100, color: colors.charcoal }}
                    />
                  )}
                  {generatedVisual.source_method && (
                    <Chip
                      icon={<BrushIcon sx={{ fontSize: 14 }} />}
                      label={SOURCE_METHOD_LABELS[generatedVisual.source_method] || generatedVisual.source_method}
                      size="small"
                      sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.72rem', fontWeight: 600, bgcolor: 'rgba(34,197,94,0.1)', color: colors.success }}
                    />
                  )}
                </Box>

                {/* Description */}
                {generatedVisual.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: colors.charcoal, mb: 0.5 }}>
                      Description du visuel
                    </Typography>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.textMed, lineHeight: 1.6, textAlign: 'justify' }}>
                      {generatedVisual.description}
                    </Typography>
                  </Box>
                )}

                {/* Brief */}
                {generatedVisual.brief && (
                  <Box sx={{ mb: 2 }}>
                    <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.8rem', color: colors.charcoal, mb: 0.5 }}>
                      Brief actionnable
                    </Typography>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        borderRadius: 1,
                        bgcolor: colors.white,
                        border: `1px solid ${colors.gray200}`,
                      }}
                    >
                      <Typography sx={{
                        fontFamily: "'Roboto Mono', 'Courier New', monospace",
                        fontSize: '0.78rem',
                        color: colors.charcoal,
                        lineHeight: 1.6,
                        whiteSpace: 'pre-wrap',
                      }}>
                        {generatedVisual.brief}
                      </Typography>
                    </Paper>
                  </Box>
                )}

                {/* Color palette + Mood */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  {generatedVisual.color_palette && generatedVisual.color_palette.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <PaletteIcon sx={{ fontSize: 14, color: colors.textMed }} />
                        <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: colors.textMed }}>
                          Palette
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {generatedVisual.color_palette.map((col, i) => (
                          <Tooltip key={i} title={col}>
                            <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: col, border: `1px solid ${colors.gray200}`, cursor: 'pointer' }} />
                          </Tooltip>
                        ))}
                      </Box>
                    </Box>
                  )}
                  {generatedVisual.mood && (
                    <Box>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600, fontSize: '0.75rem', color: colors.textMed, mb: 0.5 }}>
                        Ambiance
                      </Typography>
                      <Typography sx={{ fontFamily: '"DM Sans", sans-serif', fontSize: '0.82rem', color: colors.charcoal, fontStyle: 'italic' }}>
                        {generatedVisual.mood}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default ContentGenerator;
